/**
 * StoreListCard — the "Stores" section card shared by StorefrontSettingsPage
 * and StorefrontSettingsGuard (no-store-selected state).
 *
 * Renders every store row with its color dot, primary badge, and an Edit
 * button (EditStoreDialog), plus the Add Store row (CreateStoreDialog).
 */

import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { useStoreList, getStoreColor, type StorefrontEntry } from '@/hooks/useStoreList'
import { CreateStoreDialog } from '@/components/storefront/CreateStoreDialog'
import { EditStoreDialog } from '@/components/storefront/EditStoreDialog'
import { cn } from '@/lib/utils'
import { Pencil, Plus } from 'lucide-react'

export function StoreListCard() {
  const { data: stores = [] } = useStoreList()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editingStore, setEditingStore] = useState<StorefrontEntry | null>(null)

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
        Stores
      </h3>
      <Card className="rounded-2xl border-0">
        <CardContent className="p-0 divide-y divide-border/30">
          {stores.map((store) => (
            <div
              key={store.storefront_key}
              className="flex items-center gap-3 px-5 py-4"
            >
              <span
                className={cn(
                  'w-2.5 h-2.5 rounded-full shrink-0',
                  getStoreColor(store.color_index),
                )}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{store.label}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {store.domain}
                </p>
              </div>
              {store.is_primary ? (
                <span className="text-[10px] font-medium text-muted-foreground bg-muted rounded-full px-2 py-0.5">
                  Primary
                </span>
              ) : null}
              <button
                aria-label={`Edit ${store.label}`}
                className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                onClick={() => setEditingStore(store)}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <button
            className="flex items-center gap-3 px-5 py-4 w-full text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
            onClick={() => setCreateDialogOpen(true)}
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Add Store</span>
          </button>
        </CardContent>
      </Card>
      <CreateStoreDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
      <EditStoreDialog
        store={editingStore}
        open={editingStore !== null}
        onOpenChange={(next) => { if (!next) setEditingStore(null) }}
      />
    </div>
  )
}
